import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/authService';
import StoreService from '../../services/storeService';
import Sidebar from '../dashboard/Sidebar';
import TopNav from '../dashboard/TopNav';
import Breadcrumbs from '../dashboard/Breadcrumbs';
import IdleWarning from '../common/IdleWarning';
import useIdleLogout from '../../hooks/useIdleLogout';
import '../dashboard/Dashboard.css';

const DashboardLayout = ({ onLogout }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(null);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();


  
  const locationPathnameRef = React.useRef(location.pathname);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // .page-content is the scroll container (not the window), so the browser
  // won't reset it between pages — start each page at the top ourselves.
  const pageContentRef = React.useRef(null);
  useEffect(() => {
    if (pageContentRef.current) pageContentRef.current.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const getCurrentUser = async () => {
      setUserLoading(true);
      try {
        const sessionCheck = await AuthService.verifySession();
        if (!sessionCheck.valid) {
          navigate('/login', {
            state: {
              message: 'Your session has expired. Please log in again.',
              redirectTo: locationPathnameRef.current
            }
          });
          return;
        }
        const currentUser = await AuthService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Error getting current user:', error);
        navigate('/login', {
          state: {
            message: 'Please log in to continue',
            redirectTo: locationPathnameRef.current
          }
        });
      } finally {
        setUserLoading(false);
      }
    };
    getCurrentUser();
  }, [navigate]); // Run once on mount — not on every navigation

  const loadStores = async () => {
      if (!user) return;
    
      try {
      // Get all stores owned by current user
      const userStores = await StoreService.getMyStores();
      
      if (userStores && userStores.length > 0) {
        setStores(userStores);
        
        // Get or set active store
        let currentActiveStoreId = StoreService.getActiveStoreId();
        
        // If no active store ID or active store doesn't exist in user's stores, use first store
        if (!currentActiveStoreId || !userStores.find(s => s.$id === currentActiveStoreId)) {
          currentActiveStoreId = userStores[0].$id;
          StoreService.setActiveStoreId(currentActiveStoreId);
        }
        
        setActiveStoreId(currentActiveStoreId);
        const activeStoreData = userStores.find(s => s.$id === currentActiveStoreId) || userStores[0];
        setActiveStore(activeStoreData);
        
        // console.log('✅ Loaded stores:', userStores.length, 'Active store:', activeStoreData.storeName);
      } else {
        setStores([]);
        setActiveStore(null);
        setActiveStoreId(null);
      // console.log('User does not have a store yet. They can create one in settings.');
      }
    } catch (err) {
      console.error('Error loading stores:', err);
      setStores([]);
      setActiveStore(null);
      setActiveStoreId(null);
    }
  };

  useEffect(() => {
    loadStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Refresh stores when returning to dashboard (e.g., after creating a new store)
  useEffect(() => {
    if (location.pathname === '/dashboard' && user) {
      // Check if we need to refresh stores (e.g., after creating a new store)
      const shouldRefresh = localStorage.getItem('refreshStores') === 'true' || 
                           location.state?.refreshStores;
      
      if (shouldRefresh) {
        // console.log('🔄 Refreshing stores after store creation...');
        localStorage.removeItem('refreshStores');
        loadStores();
      } else {
        // Only load stores if we don't have them yet
        if (stores.length === 0) {
          loadStores();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state, user]);

  // Handle store switching - update context; child pages receive new activeStoreId via outlet
  const handleStoreChange = async (newStoreId) => {
    try {
      const newStore = stores.find(s => s.$id === newStoreId);
      if (newStore) {
        StoreService.setActiveStoreId(newStoreId);
        setActiveStoreId(newStoreId);
        setActiveStore(newStore);
      }
    } catch (error) {
      console.error('Error switching store:', error);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      
      // Verify session before logout (optional but good practice)
      try {
        await AuthService.verifySession();
      } catch (sessionError) {
        // console.warn('Session already invalid, proceeding with logout');
      }
      
      // Delete all sessions
      await AuthService.deleteAllSessions();
      
      // Clear localStorage
      localStorage.removeItem('userId');
      localStorage.removeItem('storeId');
      localStorage.removeItem('storeRegistrationDetails');
      localStorage.removeItem('storeLocationDetails');
      localStorage.removeItem('storePaymentDetails');
      localStorage.removeItem('locationType');
      
      if (onLogout) {
        onLogout();
      } else {
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local storage and navigate
      localStorage.clear();
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // A shop's machine is rarely one person's. Left alone, the portal signs the
  // seller out rather than leaving their orders, customers and payout numbers
  // on screen for whoever sits down next.
  const { warning: idleWarning, secondsLeft, staySignedIn } = useIdleLogout({
    enabled: !userLoading && !!user,
    onIdle: () => { handleLogout(); },
  });

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  // Toggle sidebar for mobile
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  // Generate breadcrumbs based on current path - clickable for navigation
  const generateBreadcrumbs = () => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    
    const labelMap = {
      'dashboard': t('nav.dashboard'),
      'products': t('nav.products'),
      'add': t('breadcrumbs.add'),
      'settings': t('nav.settings'),
      'orders': t('nav.orders'),
      'messages': t('nav.messages'),
      'customers': t('nav.customers'),
      'analytics': t('nav.analytics'),
      'stores': t('breadcrumbs.stores'),
      'sms': t('nav.smsSettings'),
      'payouts': t('nav.payouts'),
      'reviews': t('nav.reviews')
    };

    const breadcrumbs = [];

    parts.forEach((segment, idx) => {
      const pathParts = parts.slice(0, idx + 1);
      const fullPath = '/' + pathParts.join('/');
      const isLast = idx === parts.length - 1;
      let label = labelMap[segment];

      // Dynamic IDs: productId, orderId, customerId - use friendly label
      if (!label) {
        if (segment === 'add') label = t('nav.addStore');
        else if (parts[idx - 1] === 'products') label = t('breadcrumbs.product');
        else if (parts[idx - 1] === 'orders') label = t('breadcrumbs.order');
        else if (parts[idx - 1] === 'customers') label = t('breadcrumbs.customer');
        else label = segment.length === 24 || segment.length > 20 ? t('breadcrumbs.details') : segment.charAt(0).toUpperCase() + segment.slice(1);
      }
      
      breadcrumbs.push({
        label,
        path: isLast ? null : fullPath
      });
    });

    return breadcrumbs;
  };

  return (
    <div className="dashboard-container">
      {/* Mobile overlay for sidebar */}
      {isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}
      
      <Sidebar 
        onLogout={handleLogout} 
        loading={loading} 
        isOpen={isSidebarOpen} 
        onClose={closeSidebar} 
        unreadMessages={0}
      />

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="topnav-wrapper">
          <TopNav 
            user={user}
            onToggleSidebar={toggleSidebar}
            stores={stores}
            activeStoreId={activeStoreId}
            activeStore={activeStore}
            onStoreChange={handleStoreChange}
          />
        </div>
        <div className="breadcrumbs-wrapper">
        <Breadcrumbs paths={generateBreadcrumbs()} />
        </div>
        
        {idleWarning && (
          <IdleWarning
            secondsLeft={secondsLeft}
            onStay={staySignedIn}
            onLogoutNow={handleLogout}
          />
        )}

        <div className="page-content" ref={pageContentRef}>
          <Outlet context={{
            user,
            userLoading,
            activeStore,
            activeStoreId,
            stores,
            onUserUpdate: handleUserUpdate,
            onLogout: handleLogout,
            locationKey: location.key
          }} />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout; 